import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Plus, X, ArrowUp, ArrowDown, Save, Pencil, Check,
} from "lucide-react";
import { api, API, DOC_TYPE_LABELS, APP_TYPE_LABELS } from "../../api";
import { PageHeader, Panel, Btn, Input, Select, TextArea } from "../../ui";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// ---------------------------------------------------------------------------
// Workflow template editor
// ---------------------------------------------------------------------------
const WorkflowEditor = ({ template, onSaved }) => {
  const [stages, setStages] = useState(template.stages || []);
  const [exceptions, setExceptions] = useState(template.exceptions || []);
  const [newStage, setNewStage] = useState("");
  const [newException, setNewException] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const addStage = () => {
    const label = newStage.trim();
    if (!label) return;
    const id = slugify(label);
    if (stages.some((s) => s.id === id)) { toast.error("That stage already exists"); return; }
    setStages([...stages, { id, label }]);
    setNewStage(""); setDirty(true);
  };
  const addException = () => {
    const label = newException.trim();
    if (!label) return;
    const id = slugify(label);
    if (exceptions.some((s) => s.id === id)) { toast.error("That exception already exists"); return; }
    setExceptions([...exceptions, { id, label }]);
    setNewException(""); setDirty(true);
  };
  const removeStage = (id) => { setStages(stages.filter((s) => s.id !== id)); setDirty(true); };
  const removeException = (id) => { setExceptions(exceptions.filter((s) => s.id !== id)); setDirty(true); };
  const move = (list, setList, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next); setDirty(true);
  };
  const rename = (list, setList, id, label) => {
    setList(list.map((s) => s.id === id ? { ...s, label } : s));
    setDirty(true);
  };

  const save = async () => {
    if (stages.length === 0) { toast.error("Keep at least one stage"); return; }
    setSaving(true);
    try {
      await api.put(`${API}/templates/workflow/${template.id}`, { stages, exceptions });
      toast.success("Workflow saved");
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const Row = ({ list, setList, idx, item, kind }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(item.label);
    return (
      <li className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2 bg-white">
        <span className="text-[11px] font-mono text-slate-400 w-6 shrink-0">{idx + 1}.</span>
        {editing ? (
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            onBlur={() => { rename(list, setList, item.id, draft); setEditing(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") { rename(list, setList, item.id, draft); setEditing(false); } }}
            className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
            data-testid={`stage-rename-${item.id}`}
          />
        ) : (
          <span className="flex-1 text-sm text-slate-800" data-testid={`stage-label-${item.id}`}>{item.label}</span>
        )}
        <span className="text-[10px] font-mono text-slate-400 hidden sm:inline">{item.id}</span>
        <div className="flex gap-0.5">
          <button type="button" onClick={() => setEditing((v) => !v)} className="p-1 text-slate-500 hover:text-slate-800" title="Rename" data-testid={`stage-edit-${item.id}`}><Pencil size={13} /></button>
          <button type="button" onClick={() => move(list, setList, idx, -1)} className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30" disabled={idx === 0} title="Move up" data-testid={`stage-up-${item.id}`}><ArrowUp size={13} /></button>
          <button type="button" onClick={() => move(list, setList, idx, 1)} className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30" disabled={idx === list.length - 1} title="Move down" data-testid={`stage-down-${item.id}`}><ArrowDown size={13} /></button>
          <button type="button" onClick={() => kind === "stage" ? removeStage(item.id) : removeException(item.id)} className="p-1 text-rose-600 hover:text-rose-800" title="Remove" data-testid={`stage-remove-${item.id}`}><X size={13} /></button>
        </div>
      </li>
    );
  };

  return (
    <Panel className="p-5 space-y-5" data-testid={`workflow-editor-${template.type}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-slate-500">Application type</p>
          <h3 className="font-serif text-lg text-slate-800 mt-0.5">{APP_TYPE_LABELS[template.type] || template.type}</h3>
          <p className="text-[12px] text-slate-500 mt-1">Edit the stage order, rename, add new stages or remove ones you don't use. Changes save when you tap Save.</p>
        </div>
        <Btn onClick={save} disabled={!dirty || saving} data-testid={`workflow-save-${template.type}`}><Save size={14} /> {saving ? "Saving…" : "Save"}</Btn>
      </div>

      <div>
        <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-2">Pipeline stages (in order)</p>
        <ol className="space-y-2">
          {stages.map((s, i) => <Row key={s.id} list={stages} setList={setStages} idx={i} item={s} kind="stage" />)}
        </ol>
        <div className="flex gap-2 mt-3">
          <input value={newStage} onChange={(e) => setNewStage(e.target.value)} placeholder="New stage label…" className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm" data-testid={`workflow-new-stage-${template.type}`} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addStage())} />
          <Btn variant="ghost" onClick={addStage} data-testid={`workflow-add-stage-${template.type}`}><Plus size={13} /> Add</Btn>
        </div>
      </div>

      <div>
        <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-2">Exceptions (any case can be moved into these)</p>
        <ol className="space-y-2">
          {exceptions.map((s, i) => <Row key={s.id} list={exceptions} setList={setExceptions} idx={i} item={s} kind="exception" />)}
        </ol>
        <div className="flex gap-2 mt-3">
          <input value={newException} onChange={(e) => setNewException(e.target.value)} placeholder="e.g. On hold, Withdrawn, Declined…" className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm" data-testid={`workflow-new-exception-${template.type}`} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addException())} />
          <Btn variant="ghost" onClick={addException}><Plus size={13} /> Add</Btn>
        </div>
      </div>
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// Document checklist editor
// ---------------------------------------------------------------------------
const ChecklistEditor = ({ template, onSaved }) => {
  const [items, setItems] = useState(template.items || []);
  const [draft, setDraft] = useState({ doc_type: "id_passport", period: "", default_due_days: 7, instructions: "" });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const add = () => {
    const dt = (draft.doc_type || "").trim();
    if (!dt) return;
    if (items.some((i) => i.doc_type === dt)) { toast.error("That document is already on this checklist"); return; }
    setItems([...items, { ...draft, default_due_days: Number(draft.default_due_days) || 7 }]);
    setDraft({ doc_type: "id_passport", period: "", default_due_days: 7, instructions: "" });
    setDirty(true);
  };
  const removeAt = (idx) => { setItems(items.filter((_, i) => i !== idx)); setDirty(true); };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next); setDirty(true);
  };
  const patch = (idx, k, v) => {
    setItems(items.map((it, i) => i === idx ? { ...it, [k]: k === "default_due_days" ? Number(v) || 0 : v } : it));
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/templates/doc-checklists/${template.id}`, { items });
      toast.success("Checklist saved");
      setDirty(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Panel className="p-5 space-y-5" data-testid={`checklist-editor-${template.type}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-slate-500">Application type</p>
          <h3 className="font-serif text-lg text-slate-800 mt-0.5">{APP_TYPE_LABELS[template.type] || template.type}</h3>
          <p className="text-[12px] text-slate-500 mt-1">These items are the starting point when staff create a case. The team can still add or remove documents per case.</p>
        </div>
        <Btn onClick={save} disabled={!dirty || saving} data-testid={`checklist-save-${template.type}`}><Save size={14} /> {saving ? "Saving…" : "Save"}</Btn>
      </div>
      <ol className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="border border-slate-200 rounded p-3 space-y-2 bg-white" data-testid={`checklist-item-${template.type}-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end">
              <span className="text-[11px] font-mono text-slate-400 sm:col-span-1">{i + 1}.</span>
              <div className="sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500">Document</label>
                <select value={it.doc_type} onChange={(e) => patch(i, "doc_type", e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                  {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500">Period</label>
                <input value={it.period || ""} onChange={(e) => patch(i, "period", e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" placeholder="e.g. Last 3 months" />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500">Due (days)</label>
                <input type="number" value={it.default_due_days ?? 7} onChange={(e) => patch(i, "default_due_days", e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" min={1} />
              </div>
            </div>
            <input value={it.instructions || ""} onChange={(e) => patch(i, "instructions", e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" placeholder="Instructions shown to the customer (optional)…" />
            <div className="flex items-center justify-end gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30" disabled={i === 0}><ArrowUp size={13} /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30" disabled={i === items.length - 1}><ArrowDown size={13} /></button>
              <button type="button" onClick={() => removeAt(i)} data-testid={`checklist-remove-${template.type}-${i}`} className="p-1 text-rose-600 hover:text-rose-800"><X size={13} /></button>
            </div>
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-slate-500 text-center py-4 border border-dashed border-slate-200 rounded">No items yet — add one below.</li>}
      </ol>
      <div className="border border-slate-200 border-dashed rounded p-3 bg-slate-50 space-y-2">
        <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-slate-500">Add a document</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Select value={draft.doc_type} onChange={(e) => setDraft({ ...draft, doc_type: e.target.value })}>
            {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Input value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value })} placeholder="Period, e.g. Last 3 months" />
          <Input type="number" min={1} value={draft.default_due_days} onChange={(e) => setDraft({ ...draft, default_due_days: e.target.value })} placeholder="Due (days)" />
        </div>
        <Input value={draft.instructions} onChange={(e) => setDraft({ ...draft, instructions: e.target.value })} placeholder="Instructions (optional)" />
        <div className="flex justify-end"><Btn onClick={add} data-testid={`checklist-add-${template.type}`}><Plus size={13} /> Add item</Btn></div>
      </div>
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// Settings page
// ---------------------------------------------------------------------------
export default function SettingsPage() {
  const [me, setMe] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [privacy, setPrivacy] = useState(null);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [otp, setOtp] = useState("");
  const [tab, setTab] = useState("workflows");

  const load = async () => {
    const [m, w, c, p] = await Promise.all([
      api.get(`${API}/auth/me`),
      api.get(`${API}/templates/workflow`),
      api.get(`${API}/templates/doc-checklists`),
      api.get(`${API}/privacy/current`),
    ]);
    setMe(m.data); setWorkflows(w.data); setChecklists(c.data); setPrivacy(p.data);
  };
  useEffect(() => { load(); }, []);

  const startMfa = async () => {
    try { const { data } = await api.post(`${API}/auth/mfa/setup`); setMfaSetup(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start MFA"); }
  };
  const confirmMfa = async () => {
    try {
      await api.post(`${API}/auth/mfa/confirm`, { otp });
      toast.success("MFA enabled");
      setMfaSetup(null); setOtp(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Code didn't match"); }
  };

  if (!me) return <p className="text-sm text-slate-500">Loading…</p>;
  const isAdmin = me.role === "agency_admin";

  const TABS = [
    { id: "workflows", label: "Workflow templates" },
    { id: "checklists", label: "Document checklists" },
    { id: "privacy", label: "Privacy & security" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        subtitle={isAdmin ? "Tailor workflow stages and document checklists per case type. Changes apply the next time staff raise a case." : "Only admins can edit templates. You can still enable your MFA below."}
      />

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2 text-[11px] font-bold tracking-[0.14em] uppercase border-b-2 whitespace-nowrap ${tab === t.id ? "border-slate-800 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "workflows" && (
        <div className="space-y-5">
          {!isAdmin && <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">Read-only — admins can edit.</p>}
          {workflows.map((w) => (
            isAdmin
              ? <WorkflowEditor key={w.id} template={w} onSaved={load} />
              : <Panel key={w.id} className="p-5">
                  <p className="font-semibold text-slate-800">{APP_TYPE_LABELS[w.type] || w.type}</p>
                  <p className="text-[12px] text-slate-500 mt-2">{(w.stages || []).map((s) => s.label).join(" → ")}</p>
                </Panel>
          ))}
        </div>
      )}

      {tab === "checklists" && (
        <div className="space-y-5">
          {!isAdmin && <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">Read-only — admins can edit.</p>}
          {checklists.map((c) => (
            isAdmin
              ? <ChecklistEditor key={c.id} template={c} onSaved={load} />
              : <Panel key={c.id} className="p-5">
                  <p className="font-semibold text-slate-800">{APP_TYPE_LABELS[c.type] || c.type}</p>
                  <ul className="text-[12px] text-slate-600 mt-2 list-disc list-inside">
                    {c.items.map((i, idx) => <li key={idx}>{DOC_TYPE_LABELS[i.doc_type] || i.doc_type} · {i.period} · due in {i.default_due_days}d</li>)}
                  </ul>
                </Panel>
          ))}
        </div>
      )}

      {tab === "privacy" && (
        <div className="space-y-5">
          <Panel className="p-6">
            <h3 className="font-serif text-lg">Multi-factor authentication</h3>
            <p className="text-[12px] text-slate-500 mt-1 mb-4">Required for staff accounts. Uses any TOTP app.</p>
            {me.mfa_enabled ? <p className="text-[13px] text-emerald-700 font-semibold">MFA is enabled on your account.</p> : (
              mfaSetup ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-6">
                    <QRCodeSVG value={mfaSetup.otpauth_url} size={160} />
                    <div>
                      <p className="text-[12px] text-slate-500">Scan the QR with your authenticator app.</p>
                      <p className="text-[11px] font-mono text-slate-400 mt-2 break-all">{mfaSetup.secret}</p>
                    </div>
                  </div>
                  <Input label="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} testid="mfa-code" />
                  <Btn onClick={confirmMfa} data-testid="mfa-confirm">Enable MFA</Btn>
                </div>
              ) : <Btn onClick={startMfa} data-testid="mfa-start">Enable MFA</Btn>
            )}
          </Panel>

          <Panel className="p-6">
            <h3 className="font-serif text-lg">Privacy notice</h3>
            <p className="text-[12px] text-slate-500 mt-1 mb-3">Active version: <span className="font-mono">{privacy?.version || "—"}</span></p>
            <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{privacy?.body}</p>
          </Panel>

          <Panel className="p-6 bg-amber-50 border-amber-200">
            <h3 className="font-serif text-lg text-amber-900">Production integration still required</h3>
            <ul className="list-disc list-inside text-[13px] text-amber-900 mt-2 space-y-1">
              <li>Malware/AV scanner — files currently upload with <code>scan_status=pending</code>.</li>
            </ul>
          </Panel>
        </div>
      )}
    </div>
  );
}
